import type { AxiosResponse } from 'axios'
import JSZip from 'jszip'
import { first } from 'lodash-es'
import { last } from 'lodash-es'
import { isSafari } from 'react-device-detect'

import WebviewService from '@/service/webview.service'

import appPostMsg from './appPostMsg'
import { openLink } from './common'

export type FileDownloadT =
  | {
      fullFileName: string
    }
  | {
      fileName: string
      fileExt: string
    }

export const downloadFileByUrl = async (url: string, file: FileDownloadT) => {
  if (WebviewService.isAppAndroid) {
    const fileName = 'fullFileName' in file ? file.fullFileName : `${file.fileName}.${file.fileExt}`

    appPostMsg.exportRemoteFile(url, fileName)
  } else {
    fileDownload(url, file, '_self')
  }
}
// 여러파일을 zip으로 묶어서 다운로드
export const convertToBlobAndDownloadMultipleFilesAsZip = async (
  urls: { url: string; fullFileName: string }[],
  zipFileName: string,
) => {
  const zip = new JSZip()
  const fileNameCount = new Map<string, number>()

  // 모든 URL에서 파일을 가져와서 ZIP에 추가
  const promises = urls.map(async ({ url, fullFileName }) => {
    const response = await fetch(url)
    const blob = await response.blob()

    // 파일 이름 중복 처리
    let finalFileName = fullFileName
    const count = fileNameCount.get(fullFileName) || 0
    if (count > 0) {
      const lastDotIndex = fullFileName.lastIndexOf('.')
      const name = fullFileName.substring(0, lastDotIndex)
      const ext = fullFileName.substring(lastDotIndex)
      finalFileName = `${name} (${count})${ext}`
    }
    fileNameCount.set(fullFileName, count + 1)

    zip.file(finalFileName, blob)
  })

  await Promise.all(promises)

  // ZIP 파일 생성 및 다운로드
  const content = await zip.generateAsync({ type: 'blob' })
  const zipUrl = URL.createObjectURL(content)

  fileDownload(zipUrl, { fileName: zipFileName, fileExt: 'zip' })
}

/** 파일이름과 확장자 분리
 *  ex) "2025년.04월 25일 유형분석.보고서.pdf" -> { fileTitle: "2025년.04월 25일 유형분석.보고서", fileExt: "pdf" }
 */
export const splitFilenameAndExtension = (filename: string) => {
  const extensions = ['pdf', 'zip', 'xlsx', 'xls', 'xsl'].join('|')
  const regex = new RegExp(`^(.*)\\.(${extensions})$`, 'i')
  const match = filename.match(regex)

  if (!match) return { fileTitle: filename, fileExt: 'pdf' }
  return {
    fileTitle: match[1],
    fileExt: match[2],
  }
}

export const urlPreview = async (
  fetchUrlFn: () => Promise<{ url: string; fullFileName?: string }>,
) => {
  if (isSafari) {
    // PC 사파리에서 미리보기를 여는 경우 먼저 띄우지 않으며 팝업 차단에 의해 차단 되므로 먼저 띄우고 fetch
    const newWindow = window.open('', '_blank')
    const { url } = await fetchUrlFn()
    if (newWindow) {
      newWindow.location.href = url
    }
    return
  }
  const { url, fullFileName } = await fetchUrlFn()

  if (WebviewService.isIpad) {
    // 미리보기를 위해 forceDownload: false 설정
    fileDownload(url, { fullFileName: fullFileName ?? '미리보기' }, '_blank', {
      forceDownload: false,
    })
    return
  }

  if (WebviewService.isAndroid) {
    // 안드로이드 태블릿 브라우저 사용시 미리보기의 동작: 파일 다운로드 -> 열림
    // 따라서 동일한 파일 미리보기 할 경우 "같은 이름의 파일이 있습니다. 다시 다운로드하시겠습니까?"라는 경고 알럿이 표시되어 매번 다른 이름을 위해 blobUrl로 변환
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    openLink(blobUrl)
  } else {
    openLink(url)
  }
}

export const fileDownload = async (
  url: string,
  file: FileDownloadT,
  targetOption: '_blank' | '_self' = '_blank',
  options?: { forceDownload?: boolean },
) => {
  const downloadEl = document.createElement('a')
  downloadEl.setAttribute('target', targetOption)
  downloadEl.style.display = 'none'

  if (
    WebviewService.isIpad &&
    !url.startsWith('blob:') &&
    (('fullFileName' in file && file.fullFileName.endsWith('.pdf')) ||
      ('fileExt' in file && file.fileExt === 'pdf'))
  ) {
    // 아이패드에서 외부url로 열어서 외부 앱(굿노트 등)으로 내보내는 경우 path의 마지막 부분을 파일 이름으로 인식하므로 blobUrl 변환 후 파일 이름 지정해주어야 함
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    downloadEl.setAttribute('href', blobUrl)
  } else {
    downloadEl.setAttribute('href', url)
  }

  const shouldSetDownload = options?.forceDownload !== false
  if (shouldSetDownload) {
    if ('fullFileName' in file) {
      downloadEl.setAttribute('download', file.fullFileName)
    } else {
      downloadEl.setAttribute('download', `${file.fileName}.${file.fileExt}`)
    }
  }

  downloadEl.click()

  if (downloadEl.parentNode) {
    document.body.removeChild(downloadEl)
    URL.revokeObjectURL(url)
  }
}

// TODO: 사용하는 곳 없음
export const downloadOpenUrl = (url: string, windowOption = '_blank') => {
  // 일렉트론에서 url 미리보기시 파일 다운로드 창이 뜸 -> ?preview 가 붙은 텍스트는 일렉트론에서 다운로드 하지 않고 새 창을 열도록 예외처리할 예정
  const newUrl = `${url}?preview`
  window.open(newUrl, windowOption)
}

export function downloadXls(res: AxiosResponse<Blob>, customFileName: string) {
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }))

  const contentDisposition = res.headers['content-disposition'] // 파일 이름
  let fileName = 'unknown'
  if (contentDisposition) {
    const [fileNameMatch] = contentDisposition.split(';').filter((str) => str.includes('filename'))
    if (fileNameMatch) [, fileName] = fileNameMatch.split('=')
  }
  const fileExt = fileName.split('.')[1]?.replace(/"/g, '') || 'xsl' // 파일 확장자
  fileDownload(url, { fileName: customFileName, fileExt })
}

export function setHeaderType(response: AxiosResponse<string | Blob, any>) {
  const contentsDisposition = response.headers['content-disposition'] as string
  const fileInfo = last(contentsDisposition.split(';'))
  const filename = decodeURIComponent(first(last(fileInfo?.split("'"))?.split('.')) ?? '')
  const fileExt = last(contentsDisposition.split('.'))!.replace(/"/g, '') as 'zip' | 'pdf'
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: `application/${fileExt}` }),
  )

  return { url, filename, fileExt }
}
